// Initial professional applications share one modal boundary. Nested agreement panels
// retain their own key handling; the outer application continues to own page scrolling.
const activeModals = [];
let pageLock = null;
const LOCK_PROPERTIES = {
    "overflow-x": "hidden",
    "overflow-y": "hidden",
    "overscroll-behavior-x": "none",
    "overscroll-behavior-y": "none",
};
const focus = (element) => {
    if (element && element.isConnected) element.focus({ preventScroll: true });
};
const isTop = (modal) => activeModals[activeModals.length - 1] === modal;
function lockPage() {
    if (pageLock) return;
    pageLock = {
        x: window.scrollX,
        y: window.scrollY,
        elements: [document.documentElement, document.body].map((element) => {
            const original = element.getAttribute("style");
            const properties = Object.keys(LOCK_PROPERTIES).map((name) => ({
                name,
                value: element.style.getPropertyValue(name),
                priority: element.style.getPropertyPriority(name),
            }));
            for (const [name, value] of Object.entries(LOCK_PROPERTIES))
                element.style.setProperty(name, value, "important");
            return {
                element,
                original,
                properties,
                locked: element.getAttribute("style"),
            };
        }),
    };
}
function unlockPage() {
    if (!pageLock || activeModals.length) return;
    const saved = pageLock;
    pageLock = null;
    for (const { element, original, locked, properties } of saved.elements) {
        if (element.getAttribute("style") === locked) {
            if (original === null) element.removeAttribute("style");
            else element.setAttribute("style", original);
        } else {
            // Preserve unrelated style changes made while the application was open.
            for (const { name, value, priority } of properties) {
                if (
                    element.style.getPropertyValue(name) !==
                        LOCK_PROPERTIES[name] ||
                    element.style.getPropertyPriority(name) !== "important"
                )
                    continue;
                if (value) element.style.setProperty(name, value, priority);
                else element.style.removeProperty(name);
            }
        }
    }
    window.scrollTo(saved.x, saved.y);
}
function focusable(root) {
    return Array.from(
        root.querySelectorAll(
            "button, a[href], input, select, textarea, [tabindex]"
        )
    ).filter(
        (element) =>
            element.tabIndex >= 0 &&
            !element.matches(":disabled") &&
            element.getClientRects().length &&
            window.getComputedStyle(element).visibility !== "hidden"
    );
}
function activeRoot(modal) {
    return (
        modal.overlay.querySelector(".agreement-reader, .agreement-reminder") ||
        modal.root
    );
}
function canScrollVertically(target, overlay, deltaY) {
    let element =
        target && target.nodeType === 1
            ? target
            : target && target.parentElement;
    while (element && element !== overlay) {
        const overflow = window.getComputedStyle(element).overflowY;
        if (
            /auto|scroll/.test(overflow) &&
            element.scrollHeight > element.clientHeight
        ) {
            if (deltaY < 0 && element.scrollTop > 0) return true;
            if (
                deltaY > 0 &&
                element.scrollTop + element.clientHeight <
                    element.scrollHeight - 1
            )
                return true;
        }
        element = element.parentElement;
    }
    return false;
}
function openModal(vm) {
    if (
        vm._isDestroyed ||
        vm._isBeingDestroyed ||
        vm._applicationModal ||
        !vm.visible ||
        !vm.$refs.applicationDialog ||
        !vm.$refs.applicationOverlay
    )
        return;
    const modal = {
        root: vm.$refs.applicationDialog,
        overlay: vm.$refs.applicationOverlay,
        opener: document.activeElement,
        touch: null,
    };
    vm._applicationModal = modal;
    activeModals.push(modal);
    lockPage();
    modal.keydown = (event) => {
        if (
            !isTop(modal) ||
            event.defaultPrevented ||
            event.target.closest(".agreement-overlay")
        )
            return;
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            vm.closeDialog();
            return;
        }
        if (event.key !== "Tab") return;
        const elements = focusable(modal.root),
            first = elements[0],
            last = elements[elements.length - 1];
        if (!first) {
            event.preventDefault();
            focus(modal.root);
            return;
        }
        if (
            event.shiftKey &&
            (document.activeElement === first ||
                document.activeElement === modal.root)
        ) {
            event.preventDefault();
            focus(last);
        } else if (
            !event.shiftKey &&
            (document.activeElement === last ||
                document.activeElement === modal.root)
        ) {
            event.preventDefault();
            focus(first);
        }
    };
    modal.focusin = (event) => {
        if (!isTop(modal)) return;
        const root = activeRoot(modal);
        if (!root.contains(event.target)) focus(root);
    };
    modal.wheel = (event) => {
        if (!isTop(modal)) return;
        if (
            event.deltaX ||
            !canScrollVertically(event.target, modal.overlay, event.deltaY)
        )
            event.preventDefault();
    };
    modal.touchstart = (event) => {
        const point = event.touches.length === 1 ? event.touches[0] : null;
        modal.touch = point ? { x: point.clientX, y: point.clientY } : null;
    };
    modal.touchmove = (event) => {
        if (!isTop(modal) || !modal.touch || event.touches.length !== 1) return;
        const point = event.touches[0],
            deltaX = modal.touch.x - point.clientX,
            deltaY = modal.touch.y - point.clientY;
        modal.touch = { x: point.clientX, y: point.clientY };
        if (
            Math.abs(deltaX) > Math.abs(deltaY) ||
            !canScrollVertically(event.target, modal.overlay, deltaY)
        )
            event.preventDefault();
    };
    modal.overlay.addEventListener("keydown", modal.keydown);
    modal.overlay.addEventListener("wheel", modal.wheel, { passive: false });
    modal.overlay.addEventListener("touchstart", modal.touchstart, {
        passive: true,
    });
    modal.overlay.addEventListener("touchmove", modal.touchmove, {
        passive: false,
    });
    document.addEventListener("focusin", modal.focusin);
    focus(focusable(modal.root)[0] || modal.root);
}
function closeModal(vm) {
    const modal = vm._applicationModal;
    if (!modal) return;
    vm._applicationModal = null;
    const wasTop = isTop(modal);
    activeModals.splice(activeModals.indexOf(modal), 1);
    modal.overlay.removeEventListener("keydown", modal.keydown);
    modal.overlay.removeEventListener("wheel", modal.wheel);
    modal.overlay.removeEventListener("touchstart", modal.touchstart);
    modal.overlay.removeEventListener("touchmove", modal.touchmove);
    document.removeEventListener("focusin", modal.focusin);
    unlockPage();
    if (wasTop) {
        if (activeModals.length)
            focus(activeRoot(activeModals[activeModals.length - 1]));
        else focus(modal.opener);
    }
}
module.exports = {
    mounted() {
        if (this.visible) this.$nextTick(() => openModal(this));
    },
    watch: {
        visible(value) {
            if (value) this.$nextTick(() => openModal(this));
            else closeModal(this);
        },
    },
    beforeDestroy() {
        closeModal(this);
    },
};
